import {
    DashboardOutlined,
    TimelineOutlined,
    SettingsOutlined,
    AssessmentOutlined,
    ImportExportOutlined,
} from '@mui/icons-material'
import { BottomNavigation, BottomNavigationAction } from '@mui/material'
import { Link, useMatchRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useCalendarForTrackingsOverviewKey } from '@/helper/userSettingsKeys'
import { useBooleanUserSetting } from '@/queries/useUserSettings'

const seleniumPrefix = 'bottom-app-navigation'

/**
 * `BottomAppNavigation` is used for normal users in the application.
 * It displays the most important navigation entries along the bottom of the screen.
 */
export const BottomAppNavigation: React.FC<{ isAdmin: boolean }> = ({
    isAdmin,
}) => {
    const { data: useCalendarForTrackings } = useBooleanUserSetting(
        useCalendarForTrackingsOverviewKey
    )

    const match = useMatchRoute()
    const matchesIndex = match({ to: '/' })
    const matchesTrackings = match({
        to: useCalendarForTrackings ? '/trackings/calendar' : '/trackings/list',
    })
    const matchesStatistics = match({ to: '/statistics' })
    const matchesReports = match({ to: '/reports' })
    const matchesSettings = match({ to: '/settings' })

    const { t } = useTranslation()

    return (
        <BottomNavigation
            showLabels={true}
            sx={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,

                zIndex: 1,

                // Hide the bottom nav, when the screen doesn't match the xs breakpoint and the user is an admin.
                display: {
                    sm: 'none',
                    xs: isAdmin ? 'none' : 'flex',
                },
            }}
            value={
                // If the type is `'object'`, then the route matches
                typeof matchesIndex === 'object' ? 0
                : typeof matchesTrackings === 'object' ?
                    1
                : typeof matchesStatistics === 'object' ?
                    2
                : typeof matchesReports === 'object' ?
                    3
                : typeof matchesSettings === 'object' ?
                    4
                :   -1
            }
        >
            <BottomNavigationAction
                label={t('common.homepage')}
                icon={<DashboardOutlined />}
                component={Link}
                to={'/'}
                data-selenium={`${seleniumPrefix}-homepage`}
            />
            <BottomNavigationAction
                label={t('common.trackings')}
                icon={<TimelineOutlined />}
                component={Link}
                to={
                    useCalendarForTrackings ?
                        '/trackings/calendar'
                    :   '/trackings/list'
                }
                data-selenium={`${seleniumPrefix}-trackings`}
            />
            <BottomNavigationAction
                label={t('common.statistics')}
                icon={<AssessmentOutlined />}
                component={Link}
                to={'/statistics'}
                data-selenium={`${seleniumPrefix}-statistics`}
            />
            <BottomNavigationAction
                label={t('common.reports')}
                icon={<ImportExportOutlined />}
                component={Link}
                to={'/reports'}
                data-selenium={`${seleniumPrefix}-reports`}
            />
            <BottomNavigationAction
                label={t('common.settings')}
                icon={<SettingsOutlined />}
                component={Link}
                to={'/settings'}
                data-selenium={`${seleniumPrefix}-settings`}
            />
        </BottomNavigation>
    )
}
